"use client"

import { useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { signOut, useSession } from "next-auth/react"
import { Home, Search, Car, MessageCircle, Menu, LayoutDashboard } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { useChatUnreadCount } from "@/hooks/useChatUnreadCount"
import { useAdminStatus } from "@/hooks/useAdminStatus"

interface NavItem {
  href: string
  label: string
  icon: typeof Home
  matchPrefix?: boolean
  showIfLoggedOut?: boolean
}

function isActive(pathname: string, item: NavItem) {
  if (item.matchPrefix) return pathname.startsWith(item.href)
  return pathname === item.href
}

export function MobileBottomNav() {
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname() ?? ""
  const { data: session } = useSession()
  const chatUnreadCount = useChatUnreadCount()
  const isAdmin = useAdminStatus()

  if (pathname.startsWith("/chat") || pathname.startsWith("/offer-seats") || pathname.startsWith("/admin")) {
    return null
  }

  const handleLinkClick = () => {
    setIsOpen(false)
  }

  const items: NavItem[] = [
    { href: "/", label: "Home", icon: Home, showIfLoggedOut: true },
    { href: "/search", label: "Buscar carona", icon: Search, matchPrefix: true, showIfLoggedOut: true },
    { href: "/offer-seats", label: "Oferecer carona", icon: Car },
    { href: "/my-conversations", label: "Chat", icon: MessageCircle },
  ]

  const visibleItems = session
    ? items
    : items.filter((item) => item.showIfLoggedOut)

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
      <div className="flex items-stretch justify-around">
        {visibleItems.map((item) => {
          const active = isActive(pathname, item)
          const Icon = item.icon
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`flex flex-1 flex-col items-center gap-0.5 py-2.5 transition-colors ${
                active ? "text-blue-600" : "text-gray-600 hover:text-blue-600"
              }`}
            >
              <span className="relative">
                <Icon className="h-6 w-6" />
                {item.href === "/my-conversations" && chatUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[16px] rounded-full bg-red-500 px-1 text-[10px] text-white flex items-center justify-center">
                    {chatUnreadCount > 9 ? "9+" : chatUnreadCount}
                  </span>
                )}
              </span>
              {/* <span className="text-center text-[11px] leading-none font-medium">{item.label}</span> */}
            </Link>
          )
        })}

        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              className="flex flex-1 flex-col items-center gap-0.5 py-2.5 rounded-none text-gray-600 hover:text-blue-600 h-auto"
            >
              <Menu className="h-6 w-6" />
              {/* <span className="text-[11px] leading-none font-medium">Menu</span> */}
            </Button>
          </SheetTrigger>
          <SheetContent side="right">
            <nav className="flex flex-col space-y-4">
              <Link href="/search" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                Buscar carona
              </Link>
              {session ? (
                <>
                  <Link href="/offer-seats" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                    Oferecer carona
                  </Link>
                  <Link href="/my-rides" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                    Minhas Caronas
                  </Link>
                  <Link href="/profile" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                    Perfil
                  </Link>
                  <Link href="/my-alerts" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                    Meus alertas
                  </Link>
                  <Link href="/my-reports" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                    Minhas Denúncias
                  </Link>
                </>
              ) : (
                <>
                  <Link href="/login" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                    Entrar
                  </Link>
                  <Link href="/register" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                    Cadastrar
                  </Link>
                </>
              )}

              <Link href="/help" className="text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                Central de Ajuda
              </Link>

              {session && (
                <>
                  {isAdmin && (
                    <Link href="/admin" className="flex items-center text-sm font-medium text-gray-600 hover:text-blue-600" onClick={handleLinkClick}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  )}
                  <Link
                    href="#"
                    className="text-sm font-medium text-gray-600 hover:text-blue-600"
                    onClick={(e) => {
                      e.preventDefault()
                      handleLinkClick()
                      signOut({ callbackUrl: "/" })
                    }}
                  >
                    Sair
                  </Link>
                </>
              )}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  )
}